import React, { useState, useEffect, ChangeEvent } from 'react';
import { BodyCard } from '../components/styledComponent';
import Dropdown, { DropdownOption } from '../../components/Dropdown';
import Input, {
  EmailInput,
  FormLabeledInputRowWrapper,
  FormWrapper,
} from '../../components/forms/Input';
import FormButtonRow from '../components/formButtonRow';
import { USER_STATUS, USER_ROLE } from '../../constants';
import { testEmailAgainstRegex } from '../../lib/airframe/FormValidation';
import MessageDisplay, {
  MessageTypes,
} from '../components/styledComponent/MessageDisplay';
import styles from '../../admin/admin.module.css';
import cn from 'classnames';

interface EditUserProps {
  username: string;
  setUsername: (value: string) => void;
  emailAddress: string;
  setEmailAddress: (value: string) => void;
  password: string;
  setPassword: (value: string) => void;
  status: string;
  setStatus: (value: string) => void;
  role: string;
  setRole: (value: string) => void;
  assignedOrgNodeId: string;
  setAssignedOrgNodeId: (value: string) => void;
  assignedOrgOptions: DropdownOption<string>[];
  isCreateMode: boolean;
  onSave: () => void;
  loggedInAndEditUserSame: boolean;
}

const checkPasswordLength = (value: string, setPasswordError: (message: string) => void): boolean => {
  const minChars = 8;
  const maxChars = 30;
  const okCondition = value?.length >= minChars && value?.length <= maxChars;

  if (/\s/.test(value)) {
    setPasswordError('Password must not contain whitespace characters');
  } else if (okCondition) {
    setPasswordError('');
  } else {
    setPasswordError(`Password must be between ${minChars} and ${maxChars} characters`);
  }
  return okCondition;
};

const checkPasswordsMatch = (pass1: string, pass2: string, setConfirmationPasswordError: (message: string) => void): boolean => {
  const okCondition = pass1 === pass2;
  if (okCondition) {
    setConfirmationPasswordError('');
  } else {
    setConfirmationPasswordError("Passwords don't match.");
  }
  return okCondition;
};

const checkEmailAddress = (emailAddress: string, setEmailError: (message: string) => void): boolean => {
  const okCondition = testEmailAgainstRegex(emailAddress);
  if (okCondition) {
    setEmailError('');
  } else {
    setEmailError('Please enter a valid email address');
  }
  return okCondition;
};

const checkUsername = (username: string, setUsernameError: (message: string) => void): boolean => {
  const minUsernameLen = 1;
  const maxUsernameLen = 51;
  const okCondition = username.length >= minUsernameLen && username.length <= maxUsernameLen;
  if (okCondition) {
    setUsernameError('');
  } else {
    setUsernameError(`Please enter a username less than ${maxUsernameLen} characters.`);
  }
  return okCondition;
};
 
const EditUserForm: React.FC<EditUserProps> = ({
  username,
  setUsername,
  emailAddress,
  setEmailAddress,
  password,
  setPassword,
  status,
  setStatus,
  role,
  setRole,
  assignedOrgNodeId,
  setAssignedOrgNodeId,
  assignedOrgOptions,
  isCreateMode,
  onSave,
  loggedInAndEditUserSame
}) => {
  const statusOptions = [
    {
      value: USER_STATUS.ACTIVE,
      text: USER_STATUS.ACTIVE,
    },
    {
      value: USER_STATUS.INACTIVE,
      text: USER_STATUS.INACTIVE,
    },
  ];
  const roleOptions = [
    {
      value: USER_ROLE.AGENT,
      text: USER_ROLE.AGENT,
    },
    {
      value: USER_ROLE.ACCOUNT_ADMIN,
      text: USER_ROLE.ACCOUNT_ADMIN,
    },
  ];

  const [defaults, setDefaults] = useState({
    username: '',
    password: '',
    confirmationPassword: '',
    emailAddress: '',
    status: '',
    role: '',
    assignedOrgNodeId: '',
  });

  // by default save is disabled in create and enabled in edit ( unless there's no assignedOrgNodeId )
  const [saveDisabled, setSaveDisabled] = useState(
    isCreateMode || !assignedOrgNodeId,
  );
  const [usernameError, setUsernameError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmationPasswordError, setConfirmationPasswordError] = useState(
    '',
  );
  const [emailError, setEmailError] = useState('');
  const [confirmationPassword, setConfirmationPassword] = useState('');

  useEffect(() => {
    const localDefaults = {
      username,
      password,
      confirmationPassword,
      emailAddress,
      status,
      role,
      assignedOrgNodeId,
    };
    setDefaults(localDefaults);
  }, []);

  useEffect(() => {
    setSaveDisabled(!validateFormValues());
  }, [
    username,
    emailAddress,
    password,
    confirmationPassword,
    status,
    role,
    assignedOrgNodeId,
  ]);

  const validateFormValues = (): boolean => {
    if (isCreateMode) {
      return (
        !!username &&
        checkUsername(username, setUsernameError) &&
        !!password &&
        checkPasswordLength(password, setPasswordError) &&
        !!confirmationPassword &&
        checkPasswordsMatch(password, confirmationPassword, setConfirmationPasswordError) &&
        !!emailAddress &&
        checkEmailAddress(emailAddress, setEmailError) &&
        !!status &&
        !!role &&
        !!assignedOrgNodeId
      );
    } else {
      return !!(status && role && assignedOrgNodeId);
    }
  };

  const handleInputChange = (setter: (value: string) => void) => (event: ChangeEvent<HTMLInputElement>) => {
    setter(event.currentTarget.value);
  };

  const handleDropdownChange = (setter: (value: string) => void) => (value: string | number | readonly string[] | undefined) => {
    if (value !== null) {
      setter(value as string);
    }
  };

  const clearHandler = () => {
    setStatus(defaults.status);
    setRole(defaults.role);
    setAssignedOrgNodeId(defaults.assignedOrgNodeId);

    if (isCreateMode) {
      setUsername(defaults.username);
      setEmailAddress(defaults.emailAddress);
    }
    setPassword(defaults.password);
    setConfirmationPassword(defaults.confirmationPassword);
    setUsernameError('');
    setPasswordError('');
    setConfirmationPasswordError('');
    setEmailError('');

    setSaveDisabled(isCreateMode || !assignedOrgNodeId);
  };
  const usernameElementId = 'username';
  const emailAddressElementId = 'emailAddress';
  const statusElementId = 'status';
  const roleElementId = 'role';
  const assignedOrgElementId = 'assignedOrg';
  const passwordElementId = 'password';
  const confirmationPasswordElementId = 'confirmPassword';

  const defaultPlaceholderText = 'Select One';
  return (
    <>
      <BodyCard>
        <div data-fs-exclude className="fs-exclude">
        {loggedInAndEditUserSame && <div className={cn(styles.blink)}>
          {"Profile cannot be modified. Please contact helpdesk."}
        </div>}
        <FormWrapper>
          <FormLabeledInputRowWrapper
            elementId={usernameElementId}
            label="Username:"
          >
            
              <Input
                name={usernameElementId}
                id={usernameElementId}
                value={username}
                disabled={!isCreateMode}
                error={!!usernameError}
                onChange={handleInputChange(setUsername)}
              />
            
            {usernameError && (
              <MessageDisplay type={MessageTypes.ERROR}>
                {usernameError}
              </MessageDisplay>
            )}
          </FormLabeledInputRowWrapper>

          {isCreateMode && (
            <>
              <FormLabeledInputRowWrapper
                elementId={passwordElementId}
                label="Password:"
              >
                
                  <Input
                    type="password"
                    name={passwordElementId}
                    id={passwordElementId}
                    value={password}
                    error={!!passwordError}
                    onChange={handleInputChange(setPassword)}
                  />
                
                {passwordError && (
                  <MessageDisplay type={MessageTypes.ERROR}>
                    {passwordError}
                  </MessageDisplay>
                )}
              </FormLabeledInputRowWrapper>
              <FormLabeledInputRowWrapper
                elementId={confirmationPasswordElementId}
                label="Confirm Password:"
              >
                
                  <Input
                    type="password"
                    name={confirmationPasswordElementId}
                    id={confirmationPasswordElementId}
                    value={confirmationPassword}
                    error={!!confirmationPasswordError}
                    onChange={handleInputChange(setConfirmationPassword)}
                  />
                
                {confirmationPasswordError && (
                  <MessageDisplay type={MessageTypes.ERROR}>
                    {confirmationPasswordError}
                  </MessageDisplay>
                )}
              </FormLabeledInputRowWrapper>
            </>
          )}

          <FormLabeledInputRowWrapper
            elementId={emailAddressElementId}
            label="Email Address:"
          >
            
              <EmailInput
                name={emailAddressElementId}
                id={emailAddressElementId}
                value={emailAddress}
                disabled={!isCreateMode}
                error={!!emailError}
                onChange={handleInputChange(setEmailAddress)} // required prop, even though this input is (currently) perma-disabled
              />
            
            {emailError && (
              <MessageDisplay type={MessageTypes.ERROR}>
                {emailError}
              </MessageDisplay>
            )}
          </FormLabeledInputRowWrapper>

          <FormLabeledInputRowWrapper
            elementId={statusElementId}
            label="Status:"
          >
            <Dropdown
              id={statusElementId}
              options={statusOptions}
              placeholder={defaultPlaceholderText}
              value={status}
              onChange={handleDropdownChange(setStatus)}
              disabled={loggedInAndEditUserSame}
            />
          </FormLabeledInputRowWrapper>

          <FormLabeledInputRowWrapper elementId={roleElementId} label="Role:">
            <Dropdown
              id={roleElementId}
              options={roleOptions}
              placeholder={defaultPlaceholderText}
              value={role}
              onChange={handleDropdownChange(setRole)}
              disabled={loggedInAndEditUserSame}
            />
          </FormLabeledInputRowWrapper>

          <FormLabeledInputRowWrapper
            elementId={assignedOrgElementId}
            label="Assigned Organization:"
          >
            <Dropdown
              id={assignedOrgElementId}
              options={assignedOrgOptions}
              placeholder={defaultPlaceholderText}
              value={assignedOrgNodeId}
              onChange={handleDropdownChange(setAssignedOrgNodeId)}
              disabled={loggedInAndEditUserSame}
            />
          </FormLabeledInputRowWrapper>
        </FormWrapper>
        <FormButtonRow
          onClear={clearHandler}
          onSubmit={onSave}
          saveDisabled={saveDisabled}
          loggedInAndEditUserSame={loggedInAndEditUserSame}
          clearText="CLEAR"
          submitText="SAVE"
        />
        </div>
      </BodyCard>
    </>
  );
};

export default EditUserForm;
